import { createServerFn } from "@tanstack/react-start";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import type { VisitSource } from "./visit-source";

type Ctx = {
  supabase: { rpc: (fn: string, args: Record<string, unknown>) => Promise<{ data: unknown; error: { message: string } | null }> };
  userId: string;
};

async function assertAdmin(context: Ctx) {
  const { data, error } = await context.supabase.rpc("has_role", {
    _user_id: context.userId,
    _role: "admin",
  });
  if (error) throw new Error(error.message);
  if (!data) throw new Error("Forbidden");
}

export type ActivationStat = {
  user_id: string;
  visits: number;
  bySource: Record<VisitSource, number>;
  revenue_cents: number;
};

/**
 * Aktivace uživatelů (admin-only): počet návštěv podle zdroje (aplikace, chat,
 * e-mail, referral) a zaplacený příjem. Používá mapa i admin Doporučení, takže
 * obě místa ukazují shodná čísla.
 */
export const getActivationStats = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    await assertAdmin(context as unknown as Ctx);
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");

    const [visitRes, ordersRes] = await Promise.all([
      supabaseAdmin.from("mapa_access_log").select("user_id, source"),
      supabaseAdmin.from("orders").select("user_id, amount_cents, status").eq("status", "paid"),
    ]);

    const stats = new Map<string, ActivationStat>();
    const ensure = (id: string): ActivationStat => {
      let s = stats.get(id);
      if (!s) {
        s = { user_id: id, visits: 0, bySource: { app: 0, chat: 0, email: 0, referral: 0 }, revenue_cents: 0 };
        stats.set(id, s);
      }
      return s;
    };

    for (const v of (visitRes.data ?? []) as Array<{ user_id: string | null; source: string | null }>) {
      if (!v.user_id) continue;
      const s = ensure(v.user_id);
      s.visits += 1;
      const src = (v.source ?? "app") as VisitSource;
      if (src in s.bySource) s.bySource[src] += 1;
      else s.bySource.app += 1;
    }

    for (const o of (ordersRes.data ?? []) as Array<{ user_id: string | null; amount_cents: number | null }>) {
      if (!o.user_id) continue;
      ensure(o.user_id).revenue_cents += o.amount_cents ?? 0;
    }

    const list = [...stats.values()];
    const totals = list.reduce(
      (acc, s) => {
        acc.visits += s.visits;
        acc.revenue_cents += s.revenue_cents;
        acc.chat += s.bySource.chat;
        acc.email += s.bySource.email;
        acc.referral += s.bySource.referral;
        acc.app += s.bySource.app;
        if (s.visits > 0) acc.activeUsers += 1;
        return acc;
      },
      { visits: 0, revenue_cents: 0, chat: 0, email: 0, referral: 0, app: 0, activeUsers: 0 },
    );

    return { stats: list, totals };
  });
